import { useEffect, useRef } from "react";
import { palette } from "../../styles/themeMUI";
import { isProduction } from "../../services/serviceConfig";

const DOCUSIGN_CLIENT_KEY = "8449b970-5723-4755-b98a-6c47817816ff";
const DOCUSIGN_SCRIPT_URL = isProduction ? "https://js.docusign.com/bundle.js" : "https://js-d.docusign.com/bundle.js";
const SIGNING_STYLE = {
    branding: {
        primaryButton: {
            backgroundColor: palette.accent,
        },
        headerBackground: palette.primary.main,
    },
    signingNavigationButton: {
        finishText: "Submit contract",
        position: "bottom-center",
    },
};
let docuSignScriptPromise = null;

const loadDocuSignScript = () => {
    if (window.DocuSign) {
        return Promise.resolve();
    }

    if (docuSignScriptPromise) {
        return docuSignScriptPromise;
    }

    docuSignScriptPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${DOCUSIGN_SCRIPT_URL}"]`);

        if (existingScript) {
            existingScript.addEventListener("load", resolve, { once: true });
            existingScript.addEventListener("error", reject, { once: true });
            return;
        }

        const script = document.createElement("script");
        script.src = DOCUSIGN_SCRIPT_URL;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;

        document.body.appendChild(script);
    });

    return docuSignScriptPromise;
};

const DocuSignEmbedded = (props) => {
    const {
        signingUrl,
        onSigned,
        onDeclined,
        onError,
    } = props;
    const containerRef = useRef(null);
    const signingRef = useRef(null);

    const callbacksRef = useRef({ onSigned, onDeclined, onError });

    useEffect(() => {
        callbacksRef.current = { onSigned, onDeclined, onError };
    }, [onSigned, onDeclined, onError]);

    useEffect(() => {
        let isUnmounted = false;

        const initDocuSign = async () => {
            if (!signingUrl || !containerRef.current) {
                console.warn("[DocuSign] missing signingUrl or container");
                return;
            }

            try {
                await loadDocuSignScript();

                if (isUnmounted || !window.DocuSign || !containerRef.current) {
                    console.warn("[DocuSign] stopped after script load");
                    return;
                }

                const docuSign = await window.DocuSign.loadDocuSign(DOCUSIGN_CLIENT_KEY);

                const signing = docuSign.signing({
                    url: signingUrl,
                    displayFormat: "focused",
                    style: SIGNING_STYLE,
                });

                signing.on("ready", () => console.log("[DocuSign] ready"));

                signing.on("sessionEnd", (event) => {
                    console.log("[DocuSign] sessionEnd:", event);

                    if (event.sessionEndType === "signing_complete") callbacksRef.current.onSigned?.(event);
                    if (event.sessionEndType === "decline") callbacksRef.current.onDeclined?.(event);
                    if (event.sessionEndType === "cancel") callbacksRef.current.onCancel?.(event);
                    if (event.sessionEndType === "exception") callbacksRef.current.onError?.(event);
                    if (event.sessionEndType === "session_timeout") callbacksRef.current.onError?.(event);
                    if (event.sessionEndType === "ttl_expired") callbacksRef.current.onError?.(event);
                });

                signing.mount(containerRef.current);
                signingRef.current = signing;
            } catch (error) {
                console.error("[DocuSign] init error", error);
                callbacksRef.current.onError?.(error);
            }
        };

        initDocuSign();

        return () => {
            isUnmounted = true;
            signingRef.current?.unmount?.();
            signingRef.current = null;
        };
    }, [signingUrl]);

    return <div ref={containerRef} style={{ width: "100%", maxWidth: "1200px", height: "700px" }} />;
};

export default DocuSignEmbedded;
